import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { BoostMark } from '../../components/ui/BoostMark';
import { LinkButton } from '../../components/ui/Button';
import { brand } from '../../config/brand';
import type { BoostTier, LiveRoom } from '../rooms/api';
import { SimulcastLink } from '../rooms/Simulcast';
import { useLiveHosts } from '../rooms/useLiveHosts';
import { platform, type Explanation } from './api';

/**
 * The front door for somebody who has never been here.
 *
 * ## Why this exists again
 *
 * There was a landing page, it was deleted, and the argument for deleting it was
 * good: a site whose proposition is "there is music here" should show the music
 * rather than write a paragraph claiming it. So the dashboard became the home
 * page for everybody.
 *
 * What that missed is that the dashboard answers "what is on" and never answers
 * "what is this". A stranger landing on a wall of tracks by people they have not
 * heard of can tell it is a music site and cannot tell that the records are
 * chosen by a live human being, that anyone can host, that submitting is free,
 * or that a boost cannot buy the host's pick. Those are the things that make it
 * worth an account, and none of them are visible in a grid of artwork.
 *
 * So: this page for a stranger, the dashboard unchanged for everybody signed in.
 * `HomePage` picks. Nobody who already knows what the site is has to read the
 * explanation again.
 *
 * ## Every number on this page comes from the server
 *
 * The weights, the prices and the split are fetched from `/api/explain` rather
 * than typed into the copy. This is the front page - the most expensive place on
 * the site to be wrong - and a hardcoded "80%" outlives the setting it describes
 * by exactly one admin visit. Same argument as the console's queue legend, which
 * is generated from `Weighting` for the same reason.
 *
 * It renders without that data rather than blocking on it: the sections that
 * quote figures are the ones that wait, and the pitch above them does not.
 *
 * ## And every claim is one the code actually makes
 *
 * The mock this was built from promised artists 70% of every boost. The
 * platform pays the *host* 80% of the net and the artist nothing - a boost is a
 * listener moving a track up a host's queue, and what an artist earns from
 * championing other people is a Vibed Boost, which is not money. That is what
 * the page says, because a front page is the last place to describe a product
 * that does not exist.
 */
export function LandingPage() {
  const { items: live } = useLiveHosts();

  const { data: explain } = useQuery({
    queryKey: ['explain'],
    queryFn: () => platform.explain(),
    // The terms of the platform, not a feed. Refetching them on every focus
    // would be three requests an hour to be told the same four constants.
    staleTime: 10 * 60_000,
  });

  return (
    <div className="flex flex-col">
      <Hero live={live} explain={explain} />

      <div className="mx-auto flex w-full max-w-6xl flex-col gap-20 px-4 py-16 sm:gap-28 sm:px-6 sm:py-24">
        {/*
          Lifted over the hero's bottom edge rather than sitting below it, so the
          two read as one opening instead of a banner and then a page. The pull
          is the container's own top padding plus about thirty points, which is
          why it differs by breakpoint.
        */}
        <Facts explain={explain} />
        <HowItWorks />
        <Boosts explain={explain} />
        <OnAir live={live} />
        <Ending />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Hero                                                                */
/* ------------------------------------------------------------------ */

/**
 * The claim, and one live room beside it.
 *
 * ## The panel is real or it is an explanation
 *
 * The mock put an invented station in the hero - a name, a track, 412 people
 * listening. Shipping that would make the first thing a visitor sees the one
 * thing on the page that is false, and the fake numbers would be there on the
 * quiet Tuesday when the real answer is nobody.
 *
 * So it shows an actual broadcast when there is one, and when there is not it
 * says what a station is instead of pretending. The empty case is the common
 * one early on, which is the argument for writing it properly rather than
 * treating it as a fallback.
 */
function Hero({ live, explain }: { live: LiveRoom[]; explain?: Explanation }) {
  const room = live[0];
  const share = explain === undefined ? null : Math.round(explain.revenue.host_share_bp / 100);

  return (
    <section className="relative overflow-hidden border-b border-line">
      {/* Lit from the top left and falling away to the page's own ground, so
          the sections below read as a continuation rather than a change. */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(115% 130% at 16% 0%, color-mix(in srgb, var(--color-accent) 16%, var(--color-app)) 0%, color-mix(in srgb, var(--color-accent) 6%, var(--color-app)) 42%, var(--color-app) 78%)',
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 opacity-40"
        style={{
          backgroundImage:
            'linear-gradient(to right, color-mix(in srgb, var(--color-accent) 12%, transparent) 1px, transparent 1px)',
          backgroundSize: '60px 100%',
        }}
      />

      <div className="relative mx-auto grid w-full max-w-6xl gap-12 px-4 py-16 sm:px-6 sm:py-24 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] lg:items-center lg:gap-16">
        <div className="flex flex-col items-start gap-7">
          {live.length > 0 ? (
            <span className="flex items-center gap-2.5 rounded-[var(--radius-pill)] border border-hot-edge bg-hot-wash px-4 py-2">
              <span className="h-2 w-2 animate-pulse rounded-full bg-hot-text" aria-hidden />
              <span className="meta-caps text-hot-text">
                {live.length === 1 ? '1 station live right now' : `${live.length} stations live right now`}
              </span>
            </span>
          ) : (
            /* Not a fake live count. The quiet hour is a real state and the
               honest thing to put here is the offer instead. */
            <span className="rounded-[var(--radius-pill)] border border-line bg-raised px-4 py-2">
              <span className="meta-caps text-soft">Free to listen · no account needed</span>
            </span>
          )}

          <h1 className="text-pretty text-4xl font-bold leading-[1.05] tracking-tight text-ink sm:text-5xl lg:text-6xl">
            Radio, if every station was a person.
          </h1>

          <p className="max-w-xl text-pretty text-lg leading-relaxed text-soft">
            {brand.wordmark} is live radio run by artists and the people who listen to them. Anyone
            can host a station, anyone can submit a track to one, and what plays next is decided by
            the room and the host — never by an algorithm.
          </p>

          <div className="flex flex-wrap gap-3">
            <LinkButton to="/discover" className="px-7 py-3.5 text-base font-bold">
              Start listening
            </LinkButton>
            <LinkButton to="/register" variant="secondary" className="px-7 py-3.5 text-base">
              Put a track up
            </LinkButton>
          </div>

          {/* The share is withheld until it arrives rather than guessed at, so
              this line is never briefly wrong about somebody's money. */}
          <span className="font-mono text-[11px] text-faint">
            free to listen, no account needed
            {share !== null && ` · hosts keep ${share}% of every boost`}
          </span>
        </div>

        {room ? <LiveCard room={room} /> : <StationExplainer />}
      </div>
    </section>
  );
}

function LiveCard({ room }: { room: LiveRoom }) {
  return (
    <Link
      to={`/${room.username}`}
      data-hero-room={room.username}
      className="flex flex-col gap-5 rounded-[var(--radius-card)] border border-line bg-surface p-6 transition-colors hover:border-hot-edge"
    >
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-2">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-hot-text" aria-hidden />
          <span className="meta-caps truncate text-hot-text">
            live · {room.name ?? room.host.display_name}
          </span>
        </span>
        <span className="shrink-0 font-mono text-[10px] text-faint">
          {room.listener_count} listening
        </span>
      </div>

      <div className="flex items-center gap-4">
        {room.now_playing?.artwork_small ? (
          <img
            src={room.now_playing.artwork_small}
            alt=""
            className="h-20 w-20 shrink-0 rounded-[var(--radius-chip)] object-cover"
          />
        ) : (
          <div
            aria-hidden
            className="h-20 w-20 shrink-0 rounded-[var(--radius-chip)]"
            style={{
              backgroundImage:
                'repeating-linear-gradient(45deg,var(--color-blank) 0 8px,var(--color-blank-alt) 8px 16px)',
            }}
          />
        )}

        <div className="flex min-w-0 flex-col gap-1.5">
          {/* "Between tracks" is a real state - a host mid-crate dig has not
              stopped broadcasting - so it is said rather than left blank. */}
          <span className="truncate text-lg font-bold tracking-tight text-ink">
            {room.now_playing ? room.now_playing.title : 'Between tracks'}
          </span>
          {room.now_playing && (
            <span className="truncate font-mono text-[11px] text-soft">
              {room.now_playing.artist}
            </span>
          )}
          <span className="flex items-center gap-2 font-mono text-[10px] text-faint">
            hosted by @{room.host.username}
            <SimulcastLink simulcast={room.simulcast} size="sm" interactive={false} />
          </span>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-line pt-4 font-mono text-[10px]">
        <span className={room.submissions === 'open' ? 'text-fresh' : 'text-faint'}>
          {room.submissions === 'open'
            ? 'taking submissions'
            : room.submissions === 'followers'
              ? 'submissions from followers'
              : 'submissions closed'}
        </span>
        <span className="text-accent">drop in →</span>
      </div>
    </Link>
  );
}

/** What a station is, for the hours when none of them are on. */
function StationExplainer() {
  return (
    <div className="flex flex-col gap-5 rounded-[var(--radius-card)] border border-line bg-surface p-6">
      <span className="meta-caps text-faint">What a station is</span>

      <p className="text-pretty text-[15px] leading-relaxed text-soft">
        One person, live, playing records to whoever turns up. They queue their own crate and take
        submissions from the room — so the way onto a station is to send the host a track, not to
        be picked up by a recommender.
      </p>

      <ul className="flex flex-col gap-2.5 border-t border-line pt-4">
        <Bullet>Nobody is on air this minute. They come and go — it is live radio.</Bullet>
        <Bullet>Listening needs no account, and the chat and queue are open to read.</Bullet>
        <Bullet>Anyone can apply to host one.</Bullet>
      </ul>

      <Link to="/discover" className="font-mono text-[11px] text-accent hover:underline">
        Look through the music instead →
      </Link>
    </div>
  );
}

function Bullet({ children }: { children: ReactNode }) {
  return (
    <li className="flex gap-2.5 text-[13px] leading-relaxed text-soft">
      <span aria-hidden className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-accent" />
      {children}
    </li>
  );
}

/* ------------------------------------------------------------------ */
/* Facts                                                               */
/* ------------------------------------------------------------------ */

/**
 * Four things that are true, rather than four things that are big.
 *
 * The mock had a counter strip - 48,000 artists, $412,000 paid out, 212 stations
 * live. Invented, and the shape of the thing is the problem rather than the
 * particular lies in it: a number that is meant to impress argues *against* you
 * on the day it is small, which on a young platform is every day. These four
 * are as true in the first month as in the fiftieth.
 */
function Facts({ explain }: { explain?: Explanation }) {
  const share = explain === undefined ? null : Math.round(explain.revenue.host_share_bp / 100);

  return (
    <section className="relative z-[1] -mt-24 grid gap-px overflow-hidden rounded-[var(--radius-card)] border border-line bg-line shadow-[0_24px_60px_-40px_rgba(0,0,0,0.6)] sm:-mt-32 sm:grid-cols-2 lg:grid-cols-4">
      <Fact title="Free to listen">
        No account, no app, no trial. Rooms, chat and queues are open to read.
      </Fact>
      <Fact title="No algorithm">
        A person picks every record. What plays next is the host's call and the room's.
      </Fact>
      <Fact title={share === null ? 'The money is the host’s' : `${share}% to the host`}>
        {share === null
          ? 'What a boost pays goes to the person running the room, not to us.'
          : 'Of every boost, after processing. The platform keeps the rest.'}
      </Fact>
      <Fact title="No gatekeeper">
        No distributor, no release date, no approval queue. Upload it and it is up.
      </Fact>
    </section>
  );
}

function Fact({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2 bg-surface p-6">
      <span className="text-xl font-bold tracking-tight text-ink">{title}</span>
      <span className="text-pretty text-[13px] leading-relaxed text-soft">{children}</span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* How it works                                                        */
/* ------------------------------------------------------------------ */

function HowItWorks() {
  return (
    <section className="flex flex-col gap-9">
      <Heading eyebrow="How it works">
        Three steps between a track on your hard drive and a room full of people hearing it.
      </Heading>

      <div className="grid gap-4 lg:grid-cols-3">
        <Step
          n="01"
          tone="accent"
          title="Put a track up"
          body="No distributor, no release date, nobody to ask. Upload it and it is on your profile, playable by anyone with the link, in about a minute."
        />
        <Step
          n="02"
          tone="hot"
          title="Send it to a room that's live"
          body="Free, and to any host whose door is open. Your track joins their queue and the whole room hears it when it comes up — including the host, who can follow you on the spot."
        />
        <Step
          n="03"
          tone="fresh"
          title="The room decides what's next"
          body="Every submission is in the running. Weight moves a track up the order, being new to a room helps, and the host keeps one pick that nothing can outbid."
        />
      </div>
    </section>
  );
}

const STEP_TONE = {
  accent: { border: 'border-line-strong', text: 'text-accent', bar: 'bg-accent' },
  hot: { border: 'border-hot-edge', text: 'text-hot-text', bar: 'bg-hot-text' },
  fresh: { border: 'border-fresh-edge', text: 'text-fresh', bar: 'bg-fresh' },
} as const;

function Step({
  n,
  tone,
  title,
  body,
}: {
  n: string;
  tone: keyof typeof STEP_TONE;
  title: string;
  body: string;
}) {
  const t = STEP_TONE[tone];

  return (
    <div className={`flex flex-col gap-4 rounded-[var(--radius-card)] border ${t.border} bg-surface p-7`}>
      <div className="flex items-center justify-between">
        <span className={`font-mono text-xs ${t.text}`}>{n}</span>
        {/* The site's own motif at small size: three bars, one taller. It is
            decoration, so it is hidden rather than described. */}
        <span aria-hidden className="flex h-6 items-end gap-1">
          <span className={`w-1.5 rounded-t-[var(--radius-pill)] ${t.bar} opacity-50`} style={{ height: '45%' }} />
          <span className={`w-1.5 rounded-t-[var(--radius-pill)] ${t.bar}`} style={{ height: '100%' }} />
          <span className={`w-1.5 rounded-t-[var(--radius-pill)] ${t.bar} opacity-70`} style={{ height: '65%' }} />
        </span>
      </div>

      <span className="text-xl font-bold leading-tight tracking-tight text-ink">{title}</span>
      <p className="text-pretty text-sm leading-relaxed text-soft">{body}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Boosts                                                              */
/* ------------------------------------------------------------------ */

/**
 * The part that has to be exactly right.
 *
 * It is the only section describing what somebody's money does, so every figure
 * in it - the multipliers, the two prices, the bonuses, the tie-break, the split
 * - is rendered from `/api/explain` and none of it is written in this file. If
 * the response has not arrived the section is not drawn, which is the correct
 * failure: a pricing table missing for a second is a smaller problem than a
 * pricing table that is confidently out of date.
 */
function Boosts({ explain }: { explain?: Explanation }) {
  if (!explain) return null;

  const share = Math.round(explain.revenue.host_share_bp / 100);
  const price: Partial<Record<BoostTier, number>> = {
    basic: explain.boosts.basic_cents,
    priority: explain.boosts.priority_cents,
  };

  const NOTE: Record<BoostTier, string> = {
    free: 'always in the running — the queue is never pay-only',
    basic: 'ahead of everything unboosted',
    priority: 'the most money can do here',
    earned: 'earned, never sold — outranks both paid tiers',
    pick: "the host's own call, at any price",
  };

  return (
    <section className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]">
      <div className="flex flex-col gap-6 rounded-[var(--radius-card)] border border-line bg-surface p-7 sm:p-9">
        <Heading eyebrow="Boosts, explained" size="sm">
          Money moves you up the queue. It never buys the room.
        </Heading>

        <p className="max-w-xl text-pretty text-[15px] leading-relaxed text-soft">
          Every submission gets in line for free. A boost multiplies a track's weight so it comes up
          sooner — it does not jump the queue, and it cannot outrank a host's own pick or a boost
          somebody earned.
        </p>

        <ul className="flex flex-col gap-3">
          {explain.weighting.tiers.map((tier) => (
            <li key={tier.tier} className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
              <span className="flex items-center gap-2 text-[15px] font-bold text-ink sm:min-w-[9.5rem]">
                {tier.tier === 'earned' && <BoostMark className="h-3.5 w-3.5 shrink-0 text-fresh" />}
                {tier.label}
              </span>
              <span className="font-mono text-xs text-accent sm:min-w-[4rem]">
                {tier.weight === null ? 'pinned' : `×${tier.weight}`}
              </span>
              {/*
                Its own line on a phone, beside the weight from `sm` up. Three
                columns in 330 points leaves this one about eighty, and a note
                wrapping at four characters a line is not a note.
              */}
              <span className="w-full font-mono text-[11px] text-faint sm:w-auto sm:min-w-0 sm:flex-1">
                {price[tier.tier] !== undefined && `$${(price[tier.tier]! / 100).toFixed(2)} · `}
                {NOTE[tier.tier]}
              </span>
            </li>
          ))}
        </ul>

        {/* The two thumbs on the scale, said out loud. A rule somebody works
            out by watching an order they did not expect is a rule they assume
            was hidden on purpose. */}
        <div className="flex flex-col gap-2 border-t border-line pt-5">
          {explain.weighting.bonuses.map((bonus) => (
            <span key={bonus.label} className="font-mono text-[11px] text-soft">
              <span className="text-fresh">+{bonus.weight}</span> {bonus.label}
            </span>
          ))}
          <span className="font-mono text-[11px] text-faint">
            ties broken by {explain.weighting.tie_break}
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-5 rounded-[var(--radius-card)] border border-fresh-edge bg-fresh-wash p-7 sm:p-9">
        <span className="meta-caps text-fresh">Where a boost goes</span>

        <div className="flex flex-col gap-3">
          <span className="flex items-baseline gap-3">
            <span className="text-5xl font-bold tracking-tight text-fresh">{share}%</span>
            <span className="text-[15px] text-soft">the host running the room</span>
          </span>
          <span className="flex items-baseline gap-3">
            <span className="text-2xl font-bold tracking-tight text-soft">{100 - share}%</span>
            <span className="text-sm text-faint">the platform</span>
          </span>
        </div>

        <p className="text-pretty text-[13px] leading-relaxed text-soft">
          Of what arrives after card processing, so the fee is carried in the same proportion as the
          revenue and the host's share is the share they were promised.
        </p>

        {/*
          Said here because it is the obvious next question and the answer is
          not the flattering one. An artist is not paid by a boost on their
          track - a boost pays the host - and pretending otherwise on a front
          page is the kind of thing somebody finds out on payout day.
        */}
        <p className="flex gap-2.5 border-t border-fresh-edge pt-4 text-pretty text-[13px] leading-relaxed text-soft">
          <BoostMark className="mt-0.5 h-4 w-4 shrink-0 text-fresh" />
          <span>
            Artists earn <span className="text-ink">Vibed Boosts</span>, not a cut — by sending other
            people's tracks to hosts who play them. They are the one boost nobody can buy, and they
            outrank both paid tiers.
          </span>
        </p>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* On air                                                              */
/* ------------------------------------------------------------------ */

function OnAir({ live }: { live: LiveRoom[] }) {
  return (
    <section className="flex flex-col gap-7">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <Heading eyebrow="On air right now" size="sm" tone="hot">
          {live.length > 0 ? 'Drop into a station.' : 'Nobody is on air this minute.'}
        </Heading>
        <span className="font-mono text-[11px] text-faint">no account needed to listen</span>
      </div>

      {live.length === 0 ? (
        <div className="flex flex-col items-start gap-4 rounded-[var(--radius-card)] border border-line bg-surface p-8">
          <p className="max-w-xl text-pretty text-[15px] leading-relaxed text-soft">
            Stations are people, so they keep human hours. When one opens it appears here and in the
            header, wherever you are on the site — and the way this stops being empty is somebody
            reading it and going live.
          </p>
          <Link to="/host" className="text-sm text-accent hover:underline">
            Apply to host →
          </Link>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-landing-rooms>
          {live.slice(0, 4).map((room) => (
            <RoomCard key={room.id} room={room} />
          ))}
        </div>
      )}
    </section>
  );
}

function RoomCard({ room }: { room: LiveRoom }) {
  return (
    <Link
      to={`/${room.username}`}
      className="flex flex-col gap-4 rounded-[var(--radius-card)] border border-hot-edge bg-hot-wash p-5 transition-colors hover:border-hot-text"
    >
      <div className="flex items-center justify-between gap-2">
        {room.now_playing?.artwork_small ? (
          <img
            src={room.now_playing.artwork_small}
            alt=""
            className="h-11 w-11 rounded-[var(--radius-chip)] object-cover"
          />
        ) : (
          <div
            aria-hidden
            className="h-11 w-11 rounded-[var(--radius-chip)]"
            style={{
              backgroundImage:
                'repeating-linear-gradient(45deg,var(--color-blank) 0 6px,var(--color-blank-alt) 6px 12px)',
            }}
          />
        )}
        <span className="font-mono text-[10px] text-hot-text">{room.listener_count} listening</span>
      </div>

      <div className="flex min-w-0 flex-col gap-1">
        <span className="truncate text-[17px] font-bold tracking-tight text-ink">
          {room.name ?? room.host.display_name}
        </span>
        <span className="truncate font-mono text-[11px] text-soft">
          @{room.host.username}
          {room.genre !== null && ` · ${room.genre}`}
        </span>
      </div>

      <span className="truncate font-mono text-[10px] text-faint">
        {room.now_playing ? `now: ${room.now_playing.title}` : 'between tracks'}
      </span>
    </Link>
  );
}

/* ------------------------------------------------------------------ */
/* Ending                                                              */
/* ------------------------------------------------------------------ */

function Ending() {
  return (
    <section className="flex flex-wrap items-center justify-between gap-8 rounded-[var(--radius-card)] border border-line-strong bg-surface p-8 sm:p-12">
      <div className="flex max-w-xl flex-col gap-3">
        <h2 className="text-pretty text-3xl font-bold leading-tight tracking-tight text-ink">
          The people who would like your record are in somebody's room tonight.
        </h2>
        <p className="text-pretty text-[15px] leading-relaxed text-soft">
          Put a track up, send it to a station that is on air, and find out who is listening.
        </p>
      </div>

      <div className="flex flex-wrap gap-3">
        <LinkButton to="/register" className="px-7 py-3.5 text-base font-bold">
          Create an account
        </LinkButton>
        <LinkButton to="/discover" variant="secondary" className="px-7 py-3.5 text-base">
          Browse the music
        </LinkButton>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */

function Heading({
  eyebrow,
  size = 'lg',
  tone = 'faint',
  children,
}: {
  eyebrow: string;
  size?: 'sm' | 'lg';
  tone?: 'faint' | 'hot';
  children: ReactNode;
}) {
  return (
    <div className="flex max-w-2xl flex-col gap-3">
      <span className={`meta-caps ${tone === 'hot' ? 'text-hot-text' : 'text-faint'}`}>
        {eyebrow}
      </span>
      <h2
        className={`text-pretty font-bold leading-[1.1] tracking-tight text-ink ${
          size === 'lg' ? 'text-3xl sm:text-4xl' : 'text-2xl sm:text-3xl'
        }`}
      >
        {children}
      </h2>
    </div>
  );
}
